/**
 * Sentry — Error tracking initialization.
 *
 * Setup:
 * 1. Create a Sentry account at https://sentry.io (free tier)
 * 2. Create a new React project
 * 3. Copy the DSN and paste it below (or set VITE_SENTRY_DSN in .env)
 * 4. Errors will automatically appear in your Sentry dashboard
 */

import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || '';

// Phase 11 slot 2 (Invariant #23) — PII scrubbing.
// Widened per cross-phase sweep audit 2.1 from the original `email|token|
// password|jwt` to cover phone/ssn/dob/address/payment-card. Stack traces
// and breadcrumbs that mention these fields by NAME (e.g. error message
// "phone: 555-1234 invalid") get the value scrubbed before transmission to
// Sentry. Match `key=value` / `key: value` / `"key":"value"` shapes.
const PII_FIELD_NAMES = [
  'email', 'token', 'password', 'jwt', 'apikey', 'api_key', 'secret',
  'ssn', 'social_security',
  'phone', 'phone_number', 'mobile',
  'address', 'street', 'zip', 'postal',
  'dob', 'date_of_birth', 'birthdate',
  'card', 'card_number', 'cvv', 'cvc', 'expiry',
  'account_number', 'routing_number', 'iban',
];

// Match JSON ("key":"value"), kv (key=value), and colon (key: value) shapes.
// `"?` after the field name allows JSON closing quote between key and `:`.
// Negative class excludes string/JSON delimiters so we don't over-consume.
const PII_SCRUB_REGEX = new RegExp(
  `\\b(${PII_FIELD_NAMES.join('|')})\\b"?\\s*[=:]\\s*"?([^",\\n\\}\\]\\)\\s]+)"?`,
  'gi'
);

export function scrubPiiInString(str) {
  if (typeof str !== 'string' || str.length === 0) return str;
  PII_SCRUB_REGEX.lastIndex = 0;
  return str.replace(PII_SCRUB_REGEX, (_, field) => `${field}=[Filtered]`);
}

function scrubPiiDeep(obj, depth = 0) {
  if (depth > 8 || obj == null) return obj;
  if (typeof obj === 'string') return scrubPiiInString(obj);
  if (Array.isArray(obj)) return obj.map(item => scrubPiiDeep(item, depth + 1));
  if (typeof obj === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      out[k] = scrubPiiDeep(v, depth + 1);
    }
    return out;
  }
  return obj;
}

// Phase 11 slot 2 (cross-phase sweep audit 2.2) — per-fingerprint rate-limiter.
// Without this, an infinite-loop bug = thousands of events to Sentry, exhausting
// the free-tier 5k/mo quota. Each error fingerprint capped at 5 events/min;
// further events drop with no upstream call.
const FINGERPRINT_BUDGET = 5;
const FINGERPRINT_WINDOW_MS = 60 * 1000;
const fingerprintCounts = new Map();

function fingerprintKey(event) {
  const ex = event.exception?.values?.[0] || {};
  return `${ex.type || event.type || 'unknown'}::${(ex.value || event.message || '').slice(0, 100)}`;
}

export function shouldRateLimit(event) {
  const key = fingerprintKey(event);
  const now = Date.now();
  const entry = fingerprintCounts.get(key);
  if (!entry || now - entry.windowStart > FINGERPRINT_WINDOW_MS) {
    fingerprintCounts.set(key, { count: 1, windowStart: now });
    return false;
  }
  entry.count += 1;
  return entry.count > FINGERPRINT_BUDGET;
}

// Resolve a stable environment name from Vercel (preview/prod) or fall back to Vite mode.
// On Vercel we set VITE_APP_ENV per env; locally it's 'development'.
const SENTRY_ENV =
  import.meta.env.VITE_APP_ENV ||
  (import.meta.env.PROD ? 'production' : 'development');

export function initSentry() {
  if (!SENTRY_DSN) {
    if (import.meta.env.DEV) {
      console.log('[Sentry] No DSN configured — error tracking disabled. Set VITE_SENTRY_DSN to enable.');
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENV, // 'production' | 'staging' | 'development'
    integrations: [
      Sentry.browserTracingIntegration(),
      // Session replay — records DOM mutations + console + network per session.
      // Free tier: 50 replays/mo. Sample 10% of normal sessions, 100% of error
      // sessions. Mask all text + block media by default so we don't ship PII
      // or customer data to Sentry — only attack-surface context.
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        maskAllInputs: true,
        // Capture form field structure but not values — useful for triage,
        // safe for privacy
      }),
    ],
    // Performance tracing: 20% in prod to stay under free quota, full sampling in dev
    tracesSampleRate: SENTRY_ENV === 'production' ? 0.2 : 1.0,
    // Session replay sample rates
    //   Phase 20.4.5 (audit-fix 2026-04-29): Don's §0 Q1 lock decision —
    //   PostHog-only session replay; Sentry session replay disabled. Error
    //   sessions still captured (replaysOnErrorSampleRate=1.0). Aligns
    //   with subprocessor strategy: Sentry for errors, PostHog for product
    //   analytics + replay (DPA signed separately).
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Tag releases so source maps resolve (set by build: see vite.config.js / CI)
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    enabled: !!SENTRY_DSN,
    // Don't send PII by default; we attach user via setSentryUser on login
    sendDefaultPii: false,
    // Phase 11 slot 2 — beforeSend pipeline:
    //   1. Filter browser noise we can't act on (ResizeObserver, AbortError, etc.)
    //   2. Per-fingerprint rate-limit (max 5 events/min per error fingerprint)
    //   3. PII scrub (email/token/password/jwt/ssn/phone/address/dob/card/etc.)
    beforeSend(event) {
      const msg = event.exception?.values?.[0]?.value || '';
      // ResizeObserver loop errors — harmless browser noise
      if (msg.includes('ResizeObserver')) return null;
      // AbortError from user navigating away mid-fetch — not actionable
      if (msg.includes('AbortError') || msg.includes('The operation was aborted')) return null;
      // Network-offline errors from Supabase retries — not bugs
      if (msg.includes('NetworkError when attempting to fetch resource')) return null;
      if (msg.includes('Failed to fetch')) return null;

      // Rate-limit: drop events when a single fingerprint has fired too often
      // in the last minute. Prevents quota exhaustion under infinite-loop bugs.
      if (shouldRateLimit(event)) return null;

      // PII scrub: walk the event payload and replace `key=value` for known
      // PII keys with `key=[Filtered]`. Covers exception messages,
      // breadcrumbs, extra-data, and request bodies/headers.
      try {
        if (event.exception?.values) {
          for (const ex of event.exception.values) {
            if (ex.value) ex.value = scrubPiiInString(ex.value);
          }
        }
        if (event.message) event.message = scrubPiiInString(event.message);
        if (event.breadcrumbs) event.breadcrumbs = event.breadcrumbs.map(b => ({
          ...b,
          message: scrubPiiInString(b.message || ''),
          data: scrubPiiDeep(b.data, 0),
        }));
        if (event.extra) event.extra = scrubPiiDeep(event.extra, 0);
        if (event.request?.data) event.request.data = scrubPiiDeep(event.request.data, 0);
      } catch (scrubErr) {
        // If scrubbing fails, drop the event rather than risk leaking PII.
        // Sentry's own ingestion would otherwise see the raw event.
         
        console.warn('[sentry beforeSend] PII scrub failed; dropping event', scrubErr);
        return null;
      }

      return event;
    },
  });
}

// Re-export ErrorBoundary so callers can wrap their app without importing Sentry directly.
export const ErrorBoundary = Sentry.ErrorBoundary;

/**
 * Returns the last captured Sentry event ID, or null if none yet.
 * Used by the "Report issue" button so support@ can jump straight to the error in Sentry.
 * Gracefully returns null if Sentry isn't initialized or no events have been captured.
 */
export function getLastSentryEventId() {
  if (!SENTRY_DSN) return null;
  try {
    return Sentry.lastEventId() || null;
  } catch {
    return null;
  }
}

/**
 * Capture an error with context. Call from catch blocks.
 * No-ops gracefully if Sentry is not configured.
 */
export function captureError(error, context = {}) {
  if (!SENTRY_DSN) return;
  Sentry.withScope((scope) => {
    Object.entries(context).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    Sentry.captureException(error);
  });
}

/**
 * Set user context for error reports.
 * Call after login.
 */
export function setSentryUser(user) {
  if (!SENTRY_DSN) return;
  if (user) {
    Sentry.setUser({ id: user.id, email: user.email });
  } else {
    Sentry.setUser(null);
  }
}

// Alert-rule probe helper. Activates only when URL contains ?probe=sentry.
// See docs/runbooks/sentry-alerts.md §5 for usage. Off-by-default means
// no leakage in prod unless explicitly opted in.
if (typeof window !== 'undefined') {
  try {
    if (new URLSearchParams(window.location.search).get('probe') === 'sentry') {
      window.__juuniSentryProbe = {
        burst(n = 25) {
          for (let i = 0; i < n; i++) {
            captureError(new Error(`A1 probe ${i} @ ${Date.now()}`), { probe: 'A1' });
          }
          console.log(`[Sentry probe] Fired ${n} errors — check A1 rule within ~5 min`);
        },
        rateLimit() {
          const err = Object.assign(new Error('A3 probe — rate limit abuse'), { name: 'RateLimitError' });
          captureError(err, {
            category: 'rate_limit',
            endpoint: 'auth/signup',
            error_code: 'over_email_send_rate_limit',
            probe: 'A3',
          });
          console.log('[Sentry probe] Fired 1 RateLimitError — check A3 rule within ~5 min');
        },
      };
      console.log('[Sentry probe] Loaded. Call __juuniSentryProbe.burst(25) or .rateLimit().');
    }
  } catch { /* URL parsing noop */ }
}
